import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import type { FunctionReference, OptionalRestArgs, FunctionReturnType } from 'convex/server';

/**
 * Wrapper around Convex's useMutation that provides real isPending state tracking.
 *
 * Returns { mutateAsync, isPending } where mutateAsync wraps the Convex mutation
 * with useState-based pending tracking via try/finally.
 */
export function useConvexMutation<Mutation extends FunctionReference<"mutation">>(
  mutation: Mutation,
) {
  const rawMutate = useMutation(mutation);
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(
    async (...args: OptionalRestArgs<Mutation>): Promise<FunctionReturnType<Mutation>> => {
      setIsPending(true);
      try {
        return await rawMutate(...args);
      } finally {
        setIsPending(false);
      }
    },
    [rawMutate],
  );

  return { mutateAsync, isPending };
}
